import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  IAddWardMeasurementTypeRequest,
  IApplyMeasurementTypeTemplateRequest,
  IUpdateWardMeasurementTypeRequest,
  IWardMeasurementType,
} from "../../../@types/admin/AdminMeasurementType";
import { measurementTypesService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";
import {
  availableMeasurementTypesKey,
  wardMeasurementTypesKey,
} from "../../queries/admin/measurementTypes";

/**
 * Every write invalidates both the ward's configured list and its "available
 * to add" catalogue — the two are complements, so adding a code has to drop
 * it from the picker and removing one has to put it back.
 */
function useWardMeasurementTypeInvalidation(wardId: number | undefined) {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({
      queryKey: wardMeasurementTypesKey(wardId),
    });
    queryClient.invalidateQueries({
      queryKey: availableMeasurementTypesKey(wardId),
    });
  };
}

export function useAddWardMeasurementType(wardId: number | undefined) {
  const invalidate = useWardMeasurementTypeInvalidation(wardId);

  const { mutateAsync, isPending } = useMutation<
    IWardMeasurementType,
    Error,
    IAddWardMeasurementTypeRequest
  >({
    mutationFn: async (request) =>
      ensureOk(
        await measurementTypesService.addWardMeasurementType(wardId!, request)
      ),
    onSuccess: invalidate,
  });

  return { addWardMeasurementType: mutateAsync, isAdding: isPending };
}

export function useUpdateWardMeasurementType(wardId: number | undefined) {
  const invalidate = useWardMeasurementTypeInvalidation(wardId);

  const { mutateAsync, isPending } = useMutation<
    IWardMeasurementType,
    Error,
    { code: string; request: IUpdateWardMeasurementTypeRequest }
  >({
    mutationFn: async ({ code, request }) =>
      ensureOk(
        await measurementTypesService.updateWardMeasurementType(
          wardId!,
          code,
          request
        )
      ),
    onSuccess: invalidate,
  });

  return { updateWardMeasurementType: mutateAsync, isUpdating: isPending };
}

export function useRemoveWardMeasurementType(wardId: number | undefined) {
  const invalidate = useWardMeasurementTypeInvalidation(wardId);

  const { mutateAsync, isPending } = useMutation<
    IWardMeasurementType[],
    Error,
    string
  >({
    mutationFn: async (code) =>
      ensureOk(
        await measurementTypesService.removeWardMeasurementType(wardId!, code)
      ),
    onSuccess: invalidate,
  });

  return { removeWardMeasurementType: mutateAsync, isRemoving: isPending };
}

export function useApplyMeasurementTypeTemplate(wardId: number | undefined) {
  const invalidate = useWardMeasurementTypeInvalidation(wardId);

  const { mutateAsync, isPending } = useMutation<
    IWardMeasurementType[],
    Error,
    IApplyMeasurementTypeTemplateRequest
  >({
    mutationFn: async (request) =>
      ensureOk(
        await measurementTypesService.applyMeasurementTypeTemplate(
          wardId!,
          request
        )
      ),
    onSuccess: invalidate,
  });

  return { applyMeasurementTypeTemplate: mutateAsync, isApplying: isPending };
}
